import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { 
  HiOutlineChartBar,
  HiOutlineCalculator,
  HiOutlineCurrencyDollar,
  HiOutlineLightningBolt,
  HiOutlineCalendar,
  HiOutlineMail
} from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { api, apiUtils } from '../services/api';
import { useUserData } from '../contexts/UserDataContext';
import EmptyState from '../components/UI/EmptyState';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

// Function to create WhatsApp message for saved simulations
const createWhatsAppMessage = (simulation) => {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('es-AR', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    }).format(num);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getSimulationTypeLabel = (type) => {
    const labels = {
      'bill_coverage': 'Cobertura de Factura',
      'coverage': 'Cobertura de Factura',
      'panels': 'Número de Paneles',
      'investment': 'Monto de Inversión'
    };
    return labels[type] || type;
  };

  return `🌞 *CONSULTA SIMULACIÓN GUARDADA* 🌞

📋 *INFORMACIÓN DEL PROYECTO*
• Proyecto: ${simulation.project_name}
• Ubicación: ${simulation.project_location}
• Tipo de simulación: ${getSimulationTypeLabel(simulation.simulation_type)}

📊 *RESULTADOS DE LA SIMULACIÓN*
💰 Inversión Total: $${formatNumber(simulation.total_investment_usd)} USD
⚡ Potencia Instalada: ${formatNumber(simulation.installed_power_kw, 2)} kW
💲 Ahorro Anual (USD): $${formatNumber(simulation.annual_savings_usd, 0)}
💵 Ahorro Mensual: ${formatCurrency(simulation.monthly_savings_ars)}
📊 Retorno Anual en USD: ${formatNumber(simulation.irr_annual_usd, 1)}%
⏰ Período de Retorno: ${formatNumber(simulation.payback_period_years, 1)} años
🎯 Cobertura Lograda: ${formatNumber(simulation.bill_coverage_achieved, 1)}%

📅 *Simulación creada:* ${apiUtils.formatRelativeTime(simulation.created_at)}

Quisiera recibir asesoramiento comercial sobre esta simulación guardada. ¡Gracias! 🙌`;
};

const SimulationsPage = () => {
  const { userData, updateUserData } = useUserData();
  const [emailInput, setEmailInput] = useState(userData.email || '');
  const [showEmailForm, setShowEmailForm] = useState(!userData.email);
  
  // Fetch user simulations
  const { data: simulations, isLoading, error, refetch } = useQuery(
    ['userSimulations', userData.email],
    () => api.getUserSimulations(userData.email),
    {
      enabled: !!userData.email,
    }
  );

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput) {
      updateUserData({ email: emailInput });
      setShowEmailForm(false);
      refetch();
    }
  };
  
  // Show email input if no email is saved
  if (showEmailForm || !userData.email) {
    return (
      <div className="min-h-screen relative py-8">
        <div className="absolute inset-0">
          <img 
            src="/images/backgrounds/aereaparquesolar.jpg"
            alt="Vista aérea de parque solar"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-primary-600/70"></div>
        </div>

        <div className="container-xl section-padding relative z-10">
          <div className="max-w-md mx-auto text-center">
            <div className="card p-6">
              <HiOutlineMail className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-4">
                Ingresa tu email para ver tus simulaciones
              </h2>
              <p className="text-gray-600 mb-6">
                Necesitamos tu email para buscar tus simulaciones guardadas
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  type="email"
                  placeholder="tu@email.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button type="submit" className="btn btn-primary w-full">
                  Ver Mis Simulaciones
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen relative py-8">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="/images/backgrounds/aereaparquesolar.jpg"
          alt="Vista aérea de parque solar"
          className="w-full h-full object-cover object-center"
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-primary-600/70"></div>
      </div>

      <div className="container-xl section-padding relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-section text-white mb-4">
            Mis Simulaciones de Inversión
          </h1>
          <p className="text-lg text-gray-100">
            Revisa y compara todas tus simulaciones de inversión en proyectos solares
          </p>
          {userData.email && (
            <div className="mt-4 flex items-center space-x-2 text-gray-100">
              <HiOutlineMail className="w-5 h-5" />
              <span>{userData.email}</span>
            </div>
          )}
        </div>
        
        {/* Simulations Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="large" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error al cargar las simulaciones</p>
            <button onClick={refetch} className="btn btn-primary">
              Reintentar
            </button>
          </div>
        ) : simulations?.results?.length > 0 ? (
          <div className="space-y-6">
            {/* Simulations List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(() => {
                // Sort simulations by annual IRR in USD (highest first)
                const sortedSimulations = [...simulations.results].sort((a, b) => 
                  (b.irr_annual_usd || 0) - (a.irr_annual_usd || 0)
                );
                
                // Determine if there's a clear best return rate (not all equal)
                const returnRateValues = sortedSimulations.map(s => s.irr_annual_usd || 0);
                const hasDistinctReturn = returnRateValues.length > 1 && returnRateValues[0] > returnRateValues[1];
                
                return sortedSimulations.map((simulation, index) => (
                  <SimulationCard 
                    key={simulation.id} 
                    simulation={simulation} 
                    index={index}
                    isBestReturn={hasDistinctReturn && index === 0}
                  />
                ));
              })()}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={HiOutlineChartBar}
            title="No tienes simulaciones aún"
            description="Accede a un proyecto de Comunidad Solar o contacta a un asesor para crear tu primera simulación de inversión."
            action={
              <Link to="/" className="btn btn-primary">
                Ir al Inicio
              </Link>
            }
          />
        )}
        
        {/* Help Section */}
        <div className="mt-12 card p-6 bg-gradient-to-r from-primary-50 to-accent-50">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              ¿Necesitas ayuda con tus simulaciones?
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestro equipo está aquí para ayudarte a entender tus simulaciones y 
              tomar las mejores decisiones de inversión.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto" className="btn btn-outline">
                Contactar Asesor
              </Link>
              <Link to="/" className="btn btn-outline">
                Ir al Inicio
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simulation Card Component
const SimulationCard = ({ simulation, index, isBestReturn = false }) => {
  const TypeIcon = getSimulationTypeIcon(simulation.simulation_type);
  
  const handleWhatsAppContact = () => {
    if (!simulation.project_commercial_whatsapp) {
      toast.error('No hay número de WhatsApp configurado para este proyecto');
      return;
    }
    
    const message = createWhatsAppMessage(simulation);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${simulation.project_commercial_whatsapp}&text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`bg-white rounded-xl shadow-lg transition-all duration-300 group p-6 relative ${
        isBestReturn 
          ? 'border-4 border-yellow-600 shadow-yellow-100/20 shadow-lg ring-1 ring-yellow-500/15' 
          : 'border-2 border-white'
      }`}
    >
      {/* Best return badge */}
      {isBestReturn && (
        <div className="absolute -top-3 left-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
          🏆 Mejor Retorno
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {simulation.project_name}
          </h3>
          <p className="text-sm text-gray-600">{simulation.project_location}</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <TypeIcon className="w-4 h-4" />
          <span>{getSimulationTypeLabel(simulation.simulation_type)}</span>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Inversión Total
          </label>
          <p className="text-lg font-semibold text-gray-900">
            ${apiUtils.formatNumber(simulation.total_investment_usd)} USD
          </p>
        </div>
        
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Retorno Anual en USD
          </label>
          <p className="text-lg font-semibold text-green-600">
            {apiUtils.formatNumber(simulation.irr_annual_usd, 1)}%
          </p>
        </div>
        
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Ahorro Mensual
          </label>
          <p className="break-words text-lg font-semibold leading-tight text-primary-600">
            {apiUtils.formatCurrency(simulation.monthly_savings_ars)}
          </p>
        </div>
        
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Retorno
          </label>
          <p className="text-lg font-semibold text-gray-900">
            {apiUtils.formatNumber(simulation.payback_period_years, 1)} años
          </p>
        </div>
      </div>
      
      {/* Additional Details */}
      <div className="border-t border-gray-200 pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Potencia Instalada:</span>
          <span className="font-medium text-gray-900">{apiUtils.formatPower(simulation.installed_power_kw)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Ahorro Anual (USD):</span>
          <span className="font-medium text-gray-900">${apiUtils.formatNumber(simulation.annual_savings_usd, 0)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Cobertura Lograda:</span>
          <span className="font-medium text-accent-600">
            {apiUtils.formatNumber(simulation.bill_coverage_achieved, 1)}%
          </span>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
        <div className="flex items-center text-xs text-gray-500">
          <HiOutlineCalendar className="w-4 h-4 mr-1" />
          <span>{apiUtils.formatRelativeTime(simulation.created_at)}</span>
        </div>
        
        {/* WhatsApp Button */}
        {simulation.project_commercial_whatsapp && (
          <button
            onClick={handleWhatsAppContact}
            className="btn w-full text-sm text-white bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600 transition-colors flex items-center justify-center"
          >
            <FaWhatsapp className="w-4 h-4 mr-2" />
            Asesor Comercial
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Helper function moved inside component
function getSimulationTypeLabel(type) {
  const labels = {
    bill_coverage: 'Por Cobertura',
    coverage: 'Por Cobertura',
    panels: 'Por Paneles', 
    investment: 'Por Inversión',
  };
  return labels[type] || type;
}

function getSimulationTypeIcon(type) {
  const icons = {
    bill_coverage: HiOutlineChartBar,
    coverage: HiOutlineChartBar,
    panels: HiOutlineLightningBolt,
    investment: HiOutlineCurrencyDollar,
  };
  return icons[type] || HiOutlineCalculator;
}

export default SimulationsPage;